import React from 'react';
import { SearchableInput } from '../../ConditionEditor/components/SearchableInput';
import { useDropdown } from '../../ConditionEditor/hooks/useDropdown';
import { ARRAY_FIELDS } from '../../../constants/policy';
import { Dropdown } from '../../ConditionEditor/components/Dropdown';

/**
 * Props interface for the CountFieldSelect component
 * @property value - Currently selected field value
 * @property onChange - Callback function triggered when a new field is selected
 * @property placeholder - Optional placeholder text for the input field
 */
interface CountFieldSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * CountFieldSelect component for selecting array fields in count conditions
 * 
 * This component provides a searchable dropdown interface for selecting array fields
 * that can be used in count conditions. It supports:
 * - Field searching
 * - Dropdown selection
 * - Custom value input
 * - Infinite scrolling
 * 
 * @example
 * <CountFieldSelect
 *   value={selectedField}
 *   onChange={handleFieldChange}
 * />
 */
export const CountFieldSelect: React.FC<CountFieldSelectProps> = ({
  value,
  onChange,
  placeholder = "Search fields or enter custom value..."
}) => {
  // Reference for the input element
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Initialize dropdown state and handlers
  const {
    isOpen,
    searchTerm,
    displayCount,
    position,
    handleInputFocus,
    handleScroll,
    setSearchTerm,
    handleBlur,
    setIsOpen,
    currentValue,
    setCurrentValue
  } = useDropdown(inputRef as React.RefObject<HTMLElement>, 50, ARRAY_FIELDS.length, value);

  /**
   * Memoized filtered fields based on search term
   * Filters array fields that match the search term and limits display count
   */
  const filteredFields = React.useMemo(() => 
    ARRAY_FIELDS
      .filter(field => 
        field.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, displayCount),
    [searchTerm, displayCount]
  );

  return (
    <SearchableInput
      inputRef={inputRef as React.RefObject<HTMLInputElement>}
      value={currentValue}
      searchTerm={searchTerm}
      isOpen={isOpen}
      placeholder={placeholder}
      onChange={(val: string) => {
        setCurrentValue(val);
        onChange(val);
      }}
      onSearchChange={setSearchTerm}
      onFocus={handleInputFocus}
      onBlur={() => {
        handleBlur((val: string) => {
          setCurrentValue(val);
          onChange(val);
        });
      }}
      dropdownContent={
        <Dropdown
          isOpen={isOpen}
          position={position}
          width={inputRef.current?.offsetWidth}
          onScroll={handleScroll}
        >
          {filteredFields.map(field => (
            <div
              key={field.value}
              className="px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
              onMouseDown={() => {
                const valueWithoutParens = field.value.split(' (')[0];
                onChange(valueWithoutParens);
                setIsOpen(false);
                setSearchTerm('');
              }}
            >
              {field.display}
            </div>
          ))}
          {displayCount < ARRAY_FIELDS.length && (
            <div className="px-2 py-1 text-gray-500 text-center text-xs">
              Loading more...
            </div>
          )}
        </Dropdown>
      }
    />
  );
}; 